import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, ChartBar, Storefront, CreditCard, MagnifyingGlass,
  Trash, ShieldCheck, Crown, CaretLeft, CaretRight, Warning,
  CheckCircle, XCircle, Eye, ArrowClockwise
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = "#004E89", bgColor = "#FFD166" }) {
  return (
    <div className="bg-white border-2 border-[#004E89] rounded-2xl p-5 shadow-[4px_4px_0px_#004E89] flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center border-2 border-[#004E89]" style={{ backgroundColor: bgColor }}>
        <Icon size={24} weight="bold" color={color} />
      </div>
      <div>
        <p className="text-sm font-bold text-[#5C7A99] uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-black text-[#004E89]">{value}</p>
      </div>
    </div>
  );
}

function Pagination({ page, total, limit, onPageChange }) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-[#5C7A99] font-medium">
        Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}
      </p>
      <div className="flex gap-2">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-2 border-2 border-[#004E89] rounded-lg disabled:opacity-30 hover:bg-[#FFD166] transition-colors" data-testid="pagination-prev">
          <CaretLeft size={16} weight="bold" />
        </button>
        <span className="px-3 py-2 text-sm font-bold text-[#004E89]">{page} / {totalPages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
          className="p-2 border-2 border-[#004E89] rounded-lg disabled:opacity-30 hover:bg-[#FFD166] transition-colors" data-testid="pagination-next">
          <CaretRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}

// ---- OVERVIEW TAB ----
function OverviewTab({ stats, loading }) {
  if (loading) return <div className="text-center py-12 text-[#5C7A99] font-bold">Loading stats...</div>;
  if (!stats) return null;
  return (
    <div className="space-y-6" data-testid="admin-overview-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} />
        <StatCard icon={Crown} label="Pro Users" value={stats.premium_users} bgColor="#FF6B35" color="#fff" />
        <StatCard icon={Users} label="New (30d)" value={stats.recent_signups} bgColor="#4CB944" color="#fff" />
        <StatCard icon={ChartBar} label="Referrals" value={stats.total_referrals} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Storefront} label="Total Sales" value={stats.total_sales} />
        <StatCard icon={CheckCircle} label="Active Sales" value={stats.active_sales} bgColor="#4CB944" color="#fff" />
        <StatCard icon={Warning} label="Drafts" value={stats.draft_sales} bgColor="#FFD166" color="#004E89" />
        <StatCard icon={Eye} label="Reviews" value={stats.total_reviews} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard icon={CreditCard} label="Total Revenue" value={`$${stats.total_revenue.toFixed(2)}`} bgColor="#4CB944" color="#fff" />
        <StatCard icon={CreditCard} label="Paid Transactions" value={stats.paid_transactions} />
      </div>
    </div>
  );
}

// ---- USERS TAB ----
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const limit = 15;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (subFilter) params.subscription = subFilter;
      const { data } = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, subFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const updateSubscription = async (userId, sub, days = 30) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { subscription: sub, days }, { withCredentials: true });
      toast.success(`Subscription updated to ${sub}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
          <input type="text" placeholder="Search by name or email..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-[#004E89] rounded-xl text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
            data-testid="admin-users-search" />
        </div>
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
          className="border-2 border-[#004E89] rounded-xl px-3 py-2.5 text-sm font-bold text-[#004E89] bg-white" data-testid="admin-users-role-filter">
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        <select value={subFilter} onChange={e => { setSubFilter(e.target.value); setPage(1); }}
          className="border-2 border-[#004E89] rounded-xl px-3 py-2.5 text-sm font-bold text-[#004E89] bg-white" data-testid="admin-users-sub-filter">
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="premium">Pro</option>
        </select>
        <button onClick={fetchUsers} className="p-2.5 border-2 border-[#004E89] rounded-xl hover:bg-[#FFD166] transition-colors" data-testid="admin-users-refresh">
          <ArrowClockwise size={18} weight="bold" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#5C7A99] font-bold">Loading users...</div>
      ) : (
        <div className="overflow-x-auto border-2 border-[#004E89] rounded-2xl shadow-[4px_4px_0px_#004E89]">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="bg-[#004E89] text-white">
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Plan</th>
                <th className="text-left px-4 py-3 font-bold">Referrals</th>
                <th className="text-left px-4 py-3 font-bold">Joined</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u._id} className={`border-t border-[#004E89]/20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F6]'} hover:bg-[#FFD166]/10 transition-colors`}>
                  <td className="px-4 py-3 font-bold text-[#004E89]">{u.name || '—'}</td>
                  <td className="px-4 py-3 text-[#5C7A99]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${u.role === 'admin' ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]' : 'bg-[#004E89]/10 text-[#004E89] border-[#004E89]/30'}`}>
                      {u.role === 'admin' ? <ShieldCheck size={12} weight="bold" /> : <Users size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${u.subscription === 'premium' ? 'bg-[#4CB944]/10 text-[#4CB944] border-[#4CB944]' : 'bg-gray-100 text-gray-500 border-gray-300'}`}>
                      {u.subscription === 'premium' ? 'Pro' : 'Free'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#004E89] font-bold">{u.referral_count || 0}</td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {u.role !== 'admin' ? (
                        <button onClick={() => updateRole(u._id, 'admin')} title="Make Admin"
                          className="p-1.5 rounded-lg hover:bg-[#FF6B35]/10 text-[#FF6B35] transition-colors" data-testid={`make-admin-${u._id}`}>
                          <ShieldCheck size={16} weight="bold" />
                        </button>
                      ) : (
                        <button onClick={() => updateRole(u._id, 'user')} title="Remove Admin"
                          className="p-1.5 rounded-lg hover:bg-[#004E89]/10 text-[#004E89] transition-colors" data-testid={`remove-admin-${u._id}`}>
                          <Users size={16} weight="bold" />
                        </button>
                      )}
                      {u.subscription !== 'premium' ? (
                        <button onClick={() => updateSubscription(u._id, 'premium', 30)} title="Grant Pro (30 days)"
                          className="p-1.5 rounded-lg hover:bg-[#4CB944]/10 text-[#4CB944] transition-colors" data-testid={`grant-pro-${u._id}`}>
                          <Crown size={16} weight="bold" />
                        </button>
                      ) : (
                        <button onClick={() => updateSubscription(u._id, 'free')} title="Revoke Pro"
                          className="p-1.5 rounded-lg hover:bg-orange-100 text-orange-500 transition-colors" data-testid={`revoke-pro-${u._id}`}>
                          <XCircle size={16} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => deleteUser(u._id, u.email)} title="Delete User"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors" data-testid={`delete-user-${u._id}`}>
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#5C7A99] font-medium">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
    </div>
  );
}

// ---- SALES MODERATION TAB ----
function SalesTab() {
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const limit = 15;

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/sales`, { params, withCredentials: true });
      setSales(data.sales);
      setTotal(data.total);
    } catch (err) {
      toast.error('Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  const updateStatus = async (saleId, status) => {
    try {
      await axios.put(`${API}/admin/sales/${saleId}/status`, { status }, { withCredentials: true });
      toast.success(`Sale status updated to ${status}`);
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteSale = async (saleId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/sales/${saleId}`, { withCredentials: true });
      toast.success('Sale deleted');
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const statusColors = {
    active: 'bg-[#4CB944]/10 text-[#4CB944] border-[#4CB944]',
    draft: 'bg-[#FFD166]/20 text-[#004E89] border-[#FFD166]',
    suspended: 'bg-red-50 text-red-600 border-red-300',
    deleted: 'bg-gray-100 text-gray-500 border-gray-300',
  };

  return (
    <div className="space-y-4" data-testid="admin-sales-tab">
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
          <input type="text" placeholder="Search by title, city, or creator..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-[#004E89] rounded-xl text-sm font-medium text-[#004E89] focus:ring-2 focus:ring-[#FFD166] focus:outline-none"
            data-testid="admin-sales-search" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border-2 border-[#004E89] rounded-xl px-3 py-2.5 text-sm font-bold text-[#004E89] bg-white" data-testid="admin-sales-status-filter">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="suspended">Suspended</option>
        </select>
        <button onClick={fetchSales} className="p-2.5 border-2 border-[#004E89] rounded-xl hover:bg-[#FFD166] transition-colors" data-testid="admin-sales-refresh">
          <ArrowClockwise size={18} weight="bold" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#5C7A99] font-bold">Loading sales...</div>
      ) : (
        <div className="overflow-x-auto border-2 border-[#004E89] rounded-2xl shadow-[4px_4px_0px_#004E89]">
          <table className="w-full text-sm" data-testid="admin-sales-table">
            <thead>
              <tr className="bg-[#004E89] text-white">
                <th className="text-left px-4 py-3 font-bold">Title</th>
                <th className="text-left px-4 py-3 font-bold">Category</th>
                <th className="text-left px-4 py-3 font-bold">Location</th>
                <th className="text-left px-4 py-3 font-bold">Creator</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Payment</th>
                <th className="text-left px-4 py-3 font-bold">Date</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((s, i) => (
                <tr key={s.id} className={`border-t border-[#004E89]/20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F6]'} hover:bg-[#FFD166]/10 transition-colors`}>
                  <td className="px-4 py-3 font-bold text-[#004E89] max-w-[200px] truncate">{s.title}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#FFD166]/30 text-[#004E89] border border-[#FFD166]">
                      {(s.category || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">{s.city}, {s.state}</td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">{s.creator_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${statusColors[s.status] || statusColors.draft}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold ${s.payment_status === 'paid' ? 'text-[#4CB944]' : 'text-[#FF6B35]'}`}>
                      {s.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">{s.start_date || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {s.status !== 'active' && (
                        <button onClick={() => updateStatus(s.id, 'active')} title="Activate"
                          className="p-1.5 rounded-lg hover:bg-[#4CB944]/10 text-[#4CB944] transition-colors" data-testid={`activate-sale-${s.id}`}>
                          <CheckCircle size={16} weight="bold" />
                        </button>
                      )}
                      {s.status === 'active' && (
                        <button onClick={() => updateStatus(s.id, 'suspended')} title="Suspend"
                          className="p-1.5 rounded-lg hover:bg-orange-100 text-orange-500 transition-colors" data-testid={`suspend-sale-${s.id}`}>
                          <Warning size={16} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => deleteSale(s.id, s.title)} title="Delete"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors" data-testid={`delete-sale-${s.id}`}>
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sales.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#5C7A99] font-medium">No sales found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
    </div>
  );
}

// ---- TRANSACTIONS TAB ----
function TransactionsTab() {
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const limit = 15;

  const fetchTxns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.package_type = typeFilter;
      const { data } = await axios.get(`${API}/admin/transactions`, { params, withCredentials: true });
      setTxns(data.transactions);
      setTotal(data.total);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => { fetchTxns(); }, [fetchTxns]);

  return (
    <div className="space-y-4" data-testid="admin-transactions-tab">
      <div className="flex flex-wrap gap-3">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border-2 border-[#004E89] rounded-xl px-3 py-2.5 text-sm font-bold text-[#004E89] bg-white" data-testid="admin-txn-status-filter">
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="expired">Expired</option>
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          className="border-2 border-[#004E89] rounded-xl px-3 py-2.5 text-sm font-bold text-[#004E89] bg-white" data-testid="admin-txn-type-filter">
          <option value="">All Types</option>
          <option value="listing">Listing</option>
          <option value="vendor_listing">Vendor Event</option>
          <option value="subscription">Monthly Pro</option>
          <option value="yearly_subscription">Yearly Pro</option>
          <option value="free_trial">Free Trial</option>
          <option value="merchandise">Merchandise</option>
        </select>
        <button onClick={fetchTxns} className="p-2.5 border-2 border-[#004E89] rounded-xl hover:bg-[#FFD166] transition-colors" data-testid="admin-txn-refresh">
          <ArrowClockwise size={18} weight="bold" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#5C7A99] font-bold">Loading transactions...</div>
      ) : (
        <div className="overflow-x-auto border-2 border-[#004E89] rounded-2xl shadow-[4px_4px_0px_#004E89]">
          <table className="w-full text-sm" data-testid="admin-transactions-table">
            <thead>
              <tr className="bg-[#004E89] text-white">
                <th className="text-left px-4 py-3 font-bold">User</th>
                <th className="text-left px-4 py-3 font-bold">Package</th>
                <th className="text-left px-4 py-3 font-bold">Amount</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Discount</th>
                <th className="text-left px-4 py-3 font-bold">Date</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t, i) => (
                <tr key={t.id || t.session_id} className={`border-t border-[#004E89]/20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F9F6]'}`}>
                  <td className="px-4 py-3 text-[#004E89] font-medium text-xs">{t.user_email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#004E89]/10 text-[#004E89] border border-[#004E89]/20">
                      {(t.package_type || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-black text-[#004E89]">${(t.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${
                      t.payment_status === 'paid' ? 'bg-[#4CB944]/10 text-[#4CB944] border-[#4CB944]' :
                      t.payment_status === 'expired' ? 'bg-gray-100 text-gray-500 border-gray-300' :
                      'bg-[#FFD166]/20 text-[#004E89] border-[#FFD166]'
                    }`}>
                      {t.payment_status === 'paid' && <CheckCircle size={12} weight="bold" />}
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">
                    {t.discount_code ? `${t.discount_code} (${t.discount_percent}%)` : '—'}
                  </td>
                  <td className="px-4 py-3 text-[#5C7A99] text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C7A99] font-medium">No transactions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
    </div>
  );
}

// ---- MAIN ADMIN DASHBOARD ----
export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(data);
      } catch (err) {
        toast.error('Failed to load dashboard stats');
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (authLoading) return <div className="flex items-center justify-center min-h-screen"><div className="text-xl font-bold text-[#004E89]">Loading...</div></div>;
  if (!user || user.role !== 'admin') return <Navigate to="/explore" />;

  return (
    <div className="min-h-screen bg-[#F9F9F6] pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-[#004E89] tracking-tight" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>
          <p className="text-[#5C7A99] font-medium mt-1">Manage users, moderate sales, and track revenue</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border-2 border-[#004E89] rounded-2xl p-1.5 shadow-[4px_4px_0px_#004E89] flex flex-wrap gap-1" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="rounded-xl px-4 py-2 text-sm font-bold data-[state=active]:bg-[#004E89] data-[state=active]:text-white data-[state=active]:shadow-none text-[#004E89]" data-testid="admin-tab-overview">
              <ChartBar size={16} weight="bold" className="mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-xl px-4 py-2 text-sm font-bold data-[state=active]:bg-[#004E89] data-[state=active]:text-white data-[state=active]:shadow-none text-[#004E89]" data-testid="admin-tab-users">
              <Users size={16} weight="bold" className="mr-1.5" /> Users
            </TabsTrigger>
            <TabsTrigger value="sales" className="rounded-xl px-4 py-2 text-sm font-bold data-[state=active]:bg-[#004E89] data-[state=active]:text-white data-[state=active]:shadow-none text-[#004E89]" data-testid="admin-tab-sales">
              <Storefront size={16} weight="bold" className="mr-1.5" /> Sales
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-xl px-4 py-2 text-sm font-bold data-[state=active]:bg-[#004E89] data-[state=active]:text-white data-[state=active]:shadow-none text-[#004E89]" data-testid="admin-tab-transactions">
              <CreditCard size={16} weight="bold" className="mr-1.5" /> Transactions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab stats={stats} loading={statsLoading} /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="sales"><SalesTab /></TabsContent>
          <TabsContent value="transactions"><TransactionsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
